import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

const COLS = 10;
const ROWS = 20;
const CELL = 16;
const BOARD_W = COLS * CELL;
const SIDE_W = 110;
const CANVAS_W = BOARD_W + SIDE_W;
const CANVAS_H = ROWS * CELL;
const TICK_MS = 16;
const LOCK_DELAY = 0.5;
const MAX_LOCK_RESETS = 15;
const CLEAR_ANIM = 0.18;
const LINE_SCORES = [0, 100, 300, 500, 800];
const KICKS: Array<[number, number]> = [[0, 0], [-1, 0], [1, 0], [0, -1], [-2, 0], [2, 0], [0, -2]];

type PieceType = 'I' | 'O' | 'T' | 'S' | 'Z' | 'J' | 'L';
type Matrix = number[][];
type GameStatus = 'playing' | 'paused' | 'lost';

const SHAPES: Record<PieceType, Matrix> = {
  I: [[0, 0, 0, 0], [1, 1, 1, 1], [0, 0, 0, 0], [0, 0, 0, 0]],
  O: [[1, 1], [1, 1]],
  T: [[0, 1, 0], [1, 1, 1], [0, 0, 0]],
  S: [[0, 1, 1], [1, 1, 0], [0, 0, 0]],
  Z: [[1, 1, 0], [0, 1, 1], [0, 0, 0]],
  J: [[1, 0, 0], [1, 1, 1], [0, 0, 0]],
  L: [[0, 0, 1], [1, 1, 1], [0, 0, 0]]
};

const COLORS: Record<PieceType, string> = {
  I: '#33ff66',
  O: '#ffb000',
  T: '#b4ff5a',
  S: '#1fae4a',
  Z: '#ff8c1a',
  J: '#7dffb0',
  L: '#ffd966'
};

const PIECE_TYPES = Object.keys(SHAPES) as PieceType[];

interface Piece {
  type: PieceType;
  matrix: Matrix;
  x: number;
  y: number;
}

function rotate(matrix: Matrix, dir: 1 | -1): Matrix {
  const n = matrix.length;
  return matrix.map((row, y) => row.map((_, x) => (dir === 1 ? matrix[n - 1 - x][y] : matrix[x][n - 1 - y])));
}

/**
 * Hidden "TETRIS.EXE" easter egg: guideline-style Tetris (7-bag randomizer,
 * wall kicks, ghost piece, hold, next preview, lock delay). Only reachable in
 * terminal mode by typing "tetris" (see App.handleGlobalKeydown).
 */
@Component({
  selector: 'app-tetris-game',
  host: {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'Tetris mini-game',
    '(document:keydown)': 'onKeydown($event)',
    '(document:keyup)': 'onKeyup($event)',
    '(window:blur)': 'onBlur()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tetris-panel">
      <div class="tetris-header">
        <span class="tetris-title">*** TETRIS.EXE ***</span>
        <span class="tetris-stat">SCORE: {{ score() }}</span>
        <span class="tetris-stat">LINES: {{ lines() }}</span>
        <span class="tetris-stat">LEVEL: {{ level() }}</span>
        <button class="tetris-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas #canvasEl [width]="canvasWidth" [height]="canvasHeight" class="tetris-canvas" aria-label="Tetris board"></canvas>
      @if (status() === 'lost') {
        <div class="tetris-gameover">GAME OVER — SCORE {{ score() }} — PRESS ENTER TO RESTART</div>
      } @else {
        <div class="tetris-hint">
          ←/→ MOVE · ↓ SOFT DROP · SPACE HARD DROP · ↑/X ROTATE · Z ROTATE BACK · C HOLD · P PAUSE · ESC EXIT
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(0, 0, 0, 0.85);
      overflow: auto;
    }

    .tetris-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: 16px;
      background: #010c05;
      border: 2px solid #33ff66;
      border-radius: 4px;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.45);
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      color: #33ff66;
    }

    .tetris-header {
      display: flex;
      align-items: center;
      gap: 18px;
      width: 100%;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .tetris-title {
      font-weight: bold;
      letter-spacing: 2px;
      color: #ffb000;
      text-shadow: 0 0 6px rgba(255, 176, 0, 0.7);
    }

    .tetris-stat {
      font-size: 0.9rem;
    }

    .tetris-close {
      background: transparent;
      border: 1px solid #33ff66;
      color: #33ff66;
      font-family: inherit;
      padding: 4px 10px;
      cursor: pointer;
      border-radius: 2px;
    }

    .tetris-close:hover,
    .tetris-close:focus-visible {
      background: rgba(51, 255, 102, 0.15);
    }

    .tetris-canvas {
      width: auto;
      height: min(520px, calc(100vh - 200px));
      max-width: calc(100vw - 64px);
      image-rendering: pixelated;
      background: #000;
      border: 1px solid rgba(51, 255, 102, 0.5);
    }

    .tetris-gameover,
    .tetris-hint {
      font-size: 0.8rem;
      text-align: center;
      max-width: 520px;
    }
  `]
})
export class TetrisGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');
  private ctx: CanvasRenderingContext2D | null = null;
  private intervalId: ReturnType<typeof setInterval> | undefined;
  private lastTime = 0;

  readonly canvasWidth = CANVAS_W;
  readonly canvasHeight = CANVAS_H;

  readonly close = output<void>();
  readonly score = signal(0);
  readonly lines = signal(0);
  readonly level = signal(1);
  readonly status = signal<GameStatus>('playing');

  private board: Array<Array<PieceType | null>> = [];
  private piece: Piece | null = null;
  private bag: PieceType[] = [];
  private queue: PieceType[] = [];
  private hold: PieceType | null = null;
  private holdUsed = false;
  private gravityTimer = 0;
  private lockTimer = 0;
  private lockResets = 0;
  private softDropping = false;
  private clearingRows: number[] = [];
  private clearTimer = 0;

  ngOnInit(): void {
    this.resetState();
    this.ctx = this.canvasRef().nativeElement.getContext('2d');
    this.lastTime = performance.now();
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
  }

  ngOnDestroy(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }
    if (event.key === 'Enter' && this.status() === 'lost') {
      this.resetState();
      return;
    }
    if (event.code === 'KeyP') {
      if (this.status() !== 'lost') {
        this.status.set(this.status() === 'paused' ? 'playing' : 'paused');
      }
      return;
    }
    if (this.status() !== 'playing' || !this.piece || this.clearingRows.length > 0) {
      return;
    }
    switch (event.code) {
      case 'ArrowLeft':
      case 'KeyA':
        this.tryShift(-1);
        break;
      case 'ArrowRight':
      case 'KeyD':
        this.tryShift(1);
        break;
      case 'ArrowDown':
      case 'KeyS':
        this.softDropping = true;
        break;
      case 'ArrowUp':
      case 'KeyW':
      case 'KeyX':
        if (!event.repeat) {
          this.tryRotate(1);
        }
        break;
      case 'KeyZ':
      case 'KeyQ':
        if (!event.repeat) {
          this.tryRotate(-1);
        }
        break;
      case 'Space':
        if (!event.repeat) {
          this.hardDrop();
        }
        break;
      case 'KeyC':
      case 'ShiftLeft':
      case 'ShiftRight':
        this.holdPiece();
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  onKeyup(event: KeyboardEvent): void {
    if (event.code === 'ArrowDown' || event.code === 'KeyS') {
      this.softDropping = false;
    }
  }

  onBlur(): void {
    this.softDropping = false;
    if (this.status() === 'playing') {
      this.status.set('paused');
    }
  }

  requestClose(): void {
    this.close.emit();
  }

  private resetState(): void {
    this.board = Array.from({ length: ROWS }, () => Array<PieceType | null>(COLS).fill(null));
    this.bag = [];
    this.queue = [];
    this.hold = null;
    this.holdUsed = false;
    this.clearingRows = [];
    this.softDropping = false;
    this.score.set(0);
    this.lines.set(0);
    this.level.set(1);
    this.status.set('playing');
    this.fillQueue();
    this.spawn(this.queue.shift()!);
  }

  private fillQueue(): void {
    while (this.queue.length < 4) {
      if (this.bag.length === 0) {
        this.bag = [...PIECE_TYPES];
        for (let i = this.bag.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [this.bag[i], this.bag[j]] = [this.bag[j], this.bag[i]];
        }
      }
      this.queue.push(this.bag.pop()!);
    }
  }

  private spawn(type: PieceType): void {
    const matrix = SHAPES[type].map((row) => [...row]);
    const piece: Piece = { type, matrix, x: Math.floor((COLS - matrix.length) / 2), y: type === 'I' ? -1 : 0 };
    this.fillQueue();
    this.gravityTimer = 0;
    this.lockTimer = 0;
    this.lockResets = 0;
    if (this.collides(piece.matrix, piece.x, piece.y)) {
      this.piece = piece;
      this.status.set('lost');
      return;
    }
    this.piece = piece;
  }

  private collides(matrix: Matrix, px: number, py: number): boolean {
    for (let y = 0; y < matrix.length; y++) {
      for (let x = 0; x < matrix[y].length; x++) {
        if (!matrix[y][x]) {
          continue;
        }
        const bx = px + x;
        const by = py + y;
        if (bx < 0 || bx >= COLS || by >= ROWS) {
          return true;
        }
        if (by >= 0 && this.board[by][bx]) {
          return true;
        }
      }
    }
    return false;
  }

  private onGround(): boolean {
    return !!this.piece && this.collides(this.piece.matrix, this.piece.x, this.piece.y + 1);
  }

  /** Moving/rotating on the ground resets lock delay (capped to prevent infinite stalling). */
  private bumpLock(): void {
    if (this.onGround() && this.lockResets < MAX_LOCK_RESETS) {
      this.lockTimer = 0;
      this.lockResets++;
    }
  }

  private tryShift(dx: number): void {
    const p = this.piece!;
    if (!this.collides(p.matrix, p.x + dx, p.y)) {
      p.x += dx;
      this.bumpLock();
    }
  }

  private tryRotate(dir: 1 | -1): void {
    const p = this.piece!;
    if (p.type === 'O') {
      return;
    }
    const rotated = rotate(p.matrix, dir);
    for (const [kx, ky] of KICKS) {
      if (!this.collides(rotated, p.x + kx, p.y + ky)) {
        p.matrix = rotated;
        p.x += kx;
        p.y += ky;
        this.bumpLock();
        return;
      }
    }
  }

  private hardDrop(): void {
    const p = this.piece!;
    let dropped = 0;
    while (!this.collides(p.matrix, p.x, p.y + 1)) {
      p.y++;
      dropped++;
    }
    this.score.update((s) => s + dropped * 2);
    this.lockPiece();
  }

  private holdPiece(): void {
    if (this.holdUsed || !this.piece) {
      return;
    }
    const current = this.piece.type;
    if (this.hold) {
      const swap = this.hold;
      this.hold = current;
      this.spawn(swap);
    } else {
      this.hold = current;
      this.spawn(this.queue.shift()!);
    }
    this.holdUsed = true;
  }

  private ghostY(): number {
    const p = this.piece!;
    let y = p.y;
    while (!this.collides(p.matrix, p.x, y + 1)) {
      y++;
    }
    return y;
  }

  private gravityInterval(): number {
    return Math.max(0.05, 0.8 - (this.level() - 1) * 0.07);
  }

  private tick(): void {
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastTime) / 1000);
    this.lastTime = now;

    if (this.status() === 'playing') {
      if (this.clearingRows.length > 0) {
        this.clearTimer -= dt;
        if (this.clearTimer <= 0) {
          this.finishClear();
        }
      } else if (this.piece) {
        this.updateGravity(dt);
      }
    }
    this.draw();
  }

  private updateGravity(dt: number): void {
    const p = this.piece!;
    const interval = this.softDropping ? Math.min(0.04, this.gravityInterval()) : this.gravityInterval();
    if (this.onGround()) {
      this.lockTimer += dt;
      if (this.lockTimer >= LOCK_DELAY) {
        this.lockPiece();
      }
      return;
    }
    this.gravityTimer += dt;
    while (this.gravityTimer >= interval && !this.onGround()) {
      this.gravityTimer -= interval;
      p.y++;
      if (this.softDropping) {
        this.score.update((s) => s + 1);
      }
    }
    if (this.onGround()) {
      this.gravityTimer = 0;
    }
  }

  private lockPiece(): void {
    const p = this.piece!;
    let lockedAboveTop = false;
    p.matrix.forEach((row, y) => row.forEach((v, x) => {
      if (!v) {
        return;
      }
      const by = p.y + y;
      if (by < 0) {
        lockedAboveTop = true;
      } else {
        this.board[by][p.x + x] = p.type;
      }
    }));
    this.piece = null;
    this.holdUsed = false;
    if (lockedAboveTop) {
      this.status.set('lost');
      return;
    }
    const full = this.board.map((row, y) => (row.every((c) => c) ? y : -1)).filter((y) => y >= 0);
    if (full.length > 0) {
      this.clearingRows = full;
      this.clearTimer = CLEAR_ANIM;
    } else {
      this.spawn(this.queue.shift()!);
    }
  }

  private finishClear(): void {
    const cleared = this.clearingRows.length;
    this.board = this.board.filter((_, y) => !this.clearingRows.includes(y));
    while (this.board.length < ROWS) {
      this.board.unshift(Array<PieceType | null>(COLS).fill(null));
    }
    this.clearingRows = [];
    this.score.update((s) => s + LINE_SCORES[cleared] * this.level());
    this.lines.update((l) => l + cleared);
    this.level.set(Math.floor(this.lines() / 10) + 1);
    this.spawn(this.queue.shift()!);
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    ctx.strokeStyle = 'rgba(51, 255, 102, 0.08)';
    ctx.lineWidth = 1;
    for (let x = 1; x < COLS; x++) {
      ctx.beginPath();
      ctx.moveTo(x * CELL + 0.5, 0);
      ctx.lineTo(x * CELL + 0.5, CANVAS_H);
      ctx.stroke();
    }
    for (let y = 1; y < ROWS; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * CELL + 0.5);
      ctx.lineTo(BOARD_W, y * CELL + 0.5);
      ctx.stroke();
    }

    const flashOn = this.clearingRows.length > 0 && Math.floor(this.clearTimer * 30) % 2 === 0;
    this.board.forEach((row, y) => row.forEach((type, x) => {
      if (!type) {
        return;
      }
      if (this.clearingRows.includes(y)) {
        ctx.fillStyle = flashOn ? '#ffffff' : '#33ff66';
        ctx.fillRect(x * CELL, y * CELL, CELL, CELL);
      } else {
        this.drawCell(ctx, x * CELL, y * CELL, CELL, COLORS[type]);
      }
    }));

    if (this.piece && this.status() !== 'lost') {
      const p = this.piece;
      const gy = this.ghostY();
      ctx.strokeStyle = COLORS[p.type];
      ctx.globalAlpha = 0.4;
      this.forEachCell(p.matrix, (x, y) => {
        ctx.strokeRect((p.x + x) * CELL + 1.5, (gy + y) * CELL + 1.5, CELL - 3, CELL - 3);
      });
      ctx.globalAlpha = 1;
      this.forEachCell(p.matrix, (x, y) => {
        if (p.y + y >= 0) {
          this.drawCell(ctx, (p.x + x) * CELL, (p.y + y) * CELL, CELL, COLORS[p.type]);
        }
      });
    }

    ctx.fillStyle = '#33ff66';
    ctx.fillRect(BOARD_W, 0, 2, CANVAS_H);

    const sx = BOARD_W + 12;
    ctx.font = 'bold 10px "IBM Plex Mono", monospace';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#ffb000';
    ctx.fillText('HOLD', sx, 16);
    if (this.hold) {
      ctx.globalAlpha = this.holdUsed ? 0.35 : 1;
      this.drawPreview(ctx, this.hold, sx, 24);
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = '#ffb000';
    ctx.fillText('NEXT', sx, 92);
    this.queue.slice(0, 3).forEach((type, i) => this.drawPreview(ctx, type, sx, 100 + i * 58));

    if (this.status() !== 'playing') {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
      ctx.fillRect(0, 0, BOARD_W, CANVAS_H);
      ctx.textAlign = 'center';
      ctx.fillStyle = this.status() === 'lost' ? '#ff6b6b' : '#ffb000';
      ctx.font = 'bold 16px "IBM Plex Mono", monospace';
      ctx.fillText(this.status() === 'lost' ? 'GAME OVER' : 'PAUSED', BOARD_W / 2, CANVAS_H / 2);
      ctx.font = '9px "IBM Plex Mono", monospace';
      ctx.fillStyle = '#33ff66';
      ctx.fillText(this.status() === 'lost' ? 'ENTER TO RESTART' : 'P TO RESUME', BOARD_W / 2, CANVAS_H / 2 + 16);
    }
  }

  private forEachCell(matrix: Matrix, fn: (x: number, y: number) => void): void {
    matrix.forEach((row, y) => row.forEach((v, x) => {
      if (v) {
        fn(x, y);
      }
    }));
  }

  private drawPreview(ctx: CanvasRenderingContext2D, type: PieceType, ox: number, oy: number): void {
    const size = 12;
    const matrix = SHAPES[type];
    const cells: Array<[number, number]> = [];
    this.forEachCell(matrix, (x, y) => cells.push([x, y]));
    const minX = Math.min(...cells.map((c) => c[0]));
    const maxX = Math.max(...cells.map((c) => c[0]));
    const minY = Math.min(...cells.map((c) => c[1]));
    const maxY = Math.max(...cells.map((c) => c[1]));
    const offsetX = ox + (4 - (maxX - minX + 1)) * size / 2;
    const offsetY = oy + (3 - (maxY - minY + 1)) * size / 2;
    for (const [x, y] of cells) {
      this.drawCell(ctx, offsetX + (x - minX) * size, offsetY + (y - minY) * size, size, COLORS[type]);
    }
  }

  private drawCell(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string): void {
    ctx.fillStyle = color;
    ctx.fillRect(x, y, size, size);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.fillRect(x, y, size, 2);
    ctx.fillRect(x, y, 2, size);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fillRect(x, y + size - 2, size, 2);
    ctx.fillRect(x + size - 2, y, 2, size);
  }
}
